import type { QueryKey } from '@tanstack/react-query';

import { window } from '../../../globals';
import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { Organization } from '../../../../shared/model-typings/organization';

/**
 * Result row from the installation-wide organization search endpoint.
 *
 * Exported so other super-user-only features (eg the org-scoped Banner picker)
 * can reuse the same shape without redeclaring a local Pick alias.
 */
export type OrganizationSearchResult = Pick<Organization, 'id' | 'title' | 'access_level'>;

function searchOrganizations(query: string) {
  return get<OrganizationSearchResult[]>('/api/organizations', false, {
    search: query || undefined,
  });
}

export const getSearchOrganizationsQueryKey = (query: string): QueryKey => ['organizations', 'search', query];

/**
 * Hook to search for organizations by title or numeric ID across the whole installation.
 *
 * SUPER-USER ONLY. The backend route (`GET /api/organizations`) is guarded by
 * `helpers.admin`, which only allows `user.super`. Calling this hook as a
 * non-super user would 403, so the query is hard-gated off here as well — it
 * will never fire unless `window.user?.super` is true. Most consumers of the
 * app will not be able to use this hook; it is intended for the Admin page
 * organization switcher and other super-only surfaces (eg the Banner
 * org-scoped picker).
 *
 * @param query - The search query string (title or numeric ID)
 * @param enabled - Caller-controlled enable flag (default: true). Combined with
 *   the super-user check, so passing `true` from a non-super context still
 *   results in the query being disabled.
 */
export const useSearchOrganizations = (query: string, enabled: boolean = true) => {
  return useQuery<OrganizationSearchResult[]>({
    queryKey: getSearchOrganizationsQueryKey(query),
    queryFn: () => searchOrganizations(query),
    enabled: enabled && !!window.user?.super,
    staleTime: 30000, // Cache for 30 seconds
  });
};
